import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Prompt, useLocation } from 'react-router-dom';

// Use "globalThis" instead of "window", "self", or "global" (javascript:S7764)
const window = globalThis.window;

/**
 * Before unload event handler to prompt users about unsaved changes.
 * @param {BeforeUnloadEvent} event - The beforeunload event.
 */
const handler = event => {
  event.preventDefault();
  // Chrome requires returnValue to be set
  event.returnValue = ''; // nosonar
};

/**
 * Adds or removes the beforeunload event handler based on navigation state.
 */
const addHandler = () => {
  window.addEventListener('beforeunload', handler);
};

/**
 * Removes the beforeunload event handler.
 */
export const removeHandler = () => {
  window.removeEventListener('beforeunload', handler);
};

/**
 * NavigationPrompt component to warn users about unsaved changes
 * when they attempt to navigate away from the wizard.
 * @param {Object} props - Component props.
 * @param {boolean} props.when - Condition to trigger the prompt.
 * @param {string} props.message - Message to display in the prompt.
 * @returns {JSX.Element} The NavigationPrompt component.
 * @remarks
 * This is a React-Router v5 implementation. In React-Router v6, a different approach is needed
 */

export function NavigationPrompt({ when, message }) {
  const location = useLocation();

  useEffect(() => {
    if (!when) {
      return;
    }

    addHandler();

    return removeHandler;
  }, [when]);

  /*
   * This ensures that updating query parameters via history.push will silently
   * succeed, but navigating to a completely different page will still trigger
   * the "unsaved changes" warning. The browser-level beforeunload handler
   * (for tab close/refresh) remains active as before.
   */
  const handleBlockedNavigation = nextLocation => {
    if (!when || location.pathname === nextLocation.pathname) {
      return true;
    }

    return message;
  };

  return <Prompt when={when} message={handleBlockedNavigation} />;
}

const propTypes = {
  when: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
};

NavigationPrompt.propTypes = propTypes;
